import { useState, useEffect } from 'react'
import { useNavigate } from 'react-router-dom'
import api from '../../utils/axiosConfig'
import { useTenant } from '../../contexts/TenantContext'

const MyProfile = () => {
  const navigate = useNavigate()
  const { tenant } = useTenant()
  const [profile, setProfile] = useState(null)
  const [loading, setLoading] = useState(true)
  const [showPassword, setShowPassword] = useState(false)

  // Get theme colors based on tenant
  const getThemeColors = () => {
    const theme = tenant?.settings?.theme || tenant?.theme || 'default'

    if (theme === 'throwball' || tenant?.slug === 'tfi') {
      return {
        primary: '#c00f15',
        gradient: '#ffffff',
        primaryRgba: 'rgba(192, 15, 21, 0.1)',
        isRedTheme: true
      }
    }
    // Default ABFI theme (blue)
    return {
      primary: '#022a5e',
      gradient: 'linear-gradient(to right, #022a5e, #053e88, #022a5e)',
      primaryRgba: 'rgba(2, 42, 94, 0.1)',
      isRedTheme: false
    }
  }

  const themeColors = getThemeColors()

  useEffect(() => {
    fetchProfile()
  }, [])

  const fetchProfile = async () => {
    try {
      const response = await api.get('/user/profile')
      if (response.data.success) {
        setProfile(response.data.data)
      }
    } catch (error) {
      console.error('Error fetching profile:', error)
      if (error.response?.status !== 401) {
        alert('Failed to load profile')
      }
    } finally {
      setLoading(false)
    }
  }

  if (loading) {
    return (
      <div className="loading-container">
        <i className="fas fa-spinner fa-spin"></i>
        <p>Loading profile...</p>
        <style>{`
          .loading-container {
            display: flex;
            flex-direction: column;
            align-items: center;
            justify-content: center;
            min-height: 100vh;
            gap: 1rem;
          }
          .loading-container i {
            font-size: 3rem;
            color: var(--primary-color, #022a5e);
          }
          .loading-container p {
            color: #6b7280;
          }
        `}</style>
      </div>
    )
  }

  return (
    <div className="profile-page" style={{
      '--primary-color': themeColors.primary,
      '--primary-gradient': themeColors.gradient,
      '--primary-rgba': themeColors.primaryRgba
    }}>
      {/* Header Banner */}
      <div className={`profile-header ${themeColors.isRedTheme ? 'white-theme' : ''}`}>
        <div className="profile-avatar">
          <i className="fas fa-user"></i>
        </div>
        <div className="profile-header-info" style={{ flex: 1 }}>
          <h1>{profile?.association_name || profile?.full_name || `${profile?.first_name || ''} ${profile?.last_name || ''}`.trim() || 'User'}</h1>
          <p>View user and association details</p>
        </div>
        <button className="btn-edit" onClick={() => navigate('/user/edit-profile')}>
          <i className="fas fa-edit"></i> Edit
        </button>
        <button className="btn-back" onClick={() => navigate('/user/dashboard')}>
          <i className="fas fa-arrow-left"></i> Back
        </button>
      </div>

      {/* Quick Info Cards */}
      <div className="quick-info-cards">
        <div className="info-card">
          <div className="info-card-icon">
            <i className="fas fa-building"></i>
          </div>
          <div className="info-card-content">
            <h3>{profile?.association_name || '-'}</h3>
            <p>Association</p>
          </div>
        </div>

        <div className="info-card">
          <div className="info-card-icon">
            <i className="fas fa-map-marker-alt"></i>
          </div>
          <div className="info-card-content">
            <h3>{profile?.state || '-'}</h3>
            <p>State</p>
          </div>
        </div>

        <div className="info-card">
          <div className="info-card-icon status-icon">
            <i className="fas fa-check-circle"></i>
          </div>
          <div className="info-card-content">
            <span className={`status-badge ${profile?.status || 'active'}`}>
              {profile?.status || 'Active'}
            </span>
            <p>Account Status</p>
          </div>
        </div>
      </div>

      {/* Profile Content */}
      <div className="profile-content">
        {/* Association Information */}
        <div className="detail-section">
          <h3><i className="fas fa-building"></i> Association Information</h3>
          <div className="detail-grid">
            <div className="detail-item full-width">
              <label>ASSOCIATION NAME</label>
              <span className="detail-value">{profile?.association_name || '-'}</span>
            </div>
            <div className="detail-item">
              <label>STATE</label>
              <span className="detail-value state-badge">{profile?.state || '-'}</span>
            </div>
            <div className="detail-item">
              <label>PRESIDENT</label>
              <span className="detail-value">{profile?.president || '-'}</span>
            </div>
            <div className="detail-item">
              <label>SECRETARY</label>
              <span className="detail-value">{profile?.secretary || '-'}</span>
            </div>
          </div>
        </div>

        {/* Contact Information */}
        <div className="detail-section">
          <h3><i className="fas fa-phone"></i> Contact Information</h3>
          <div className="detail-grid">
            <div className="detail-item">
              <label>OFFICIAL CONTACT NO</label>
              <span className="detail-value">
                <i className="fas fa-phone icon-sm"></i>
                {profile?.official_contact || profile?.phone || '-'}
              </span>
            </div>
            <div className="detail-item">
              <label>OFFICIAL EMAIL ID</label>
              <span className="detail-value">
                <i className="fas fa-envelope icon-sm"></i>
                {profile?.official_email || profile?.email || '-'}
              </span>
            </div>
            <div className="detail-item full-width">
              <label>ADDRESS</label>
              <span className="detail-value">
                <i className="fas fa-map-marker-alt icon-sm"></i>
                {profile?.address || '-'}
              </span>
            </div>
          </div>
        </div>

        {/* Login Credentials */}
        <div className="detail-section">
          <h3><i className="fas fa-lock"></i> Login Credentials</h3>
          <div className="detail-grid">
            <div className="detail-item">
              <label>USERNAME</label>
              <span className="detail-value username-badge">
                <i className="fas fa-user icon-sm"></i>
                {profile?.username || '-'}
              </span>
            </div>
            <div className="detail-item">
              <label>PASSWORD</label>
              <div className="password-field">
                <span className="detail-value password-value">
                  <i className="fas fa-lock icon-sm"></i>
                  {showPassword
                    ? (profile?.plain_password || 'Password not stored')
                    : '••••••••'}
                </span>
                <button
                  className="toggle-password-btn"
                  onClick={() => setShowPassword(!showPassword)}
                  title={showPassword ? 'Hide Password' : 'Show Password'}
                >
                  <i className={`fas ${showPassword ? 'fa-eye-slash' : 'fa-eye'}`}></i>
                </button>
              </div>
            </div>
          </div>
        </div>

        {/* Account Information */}
        <div className="detail-section">
          <h3><i className="fas fa-info-circle"></i> Account Information</h3>
          <div className="detail-grid">
            <div className="detail-item">
              <label>ROLE</label>
              <span className="detail-value role-badge">{profile?.role || 'User'}</span>
            </div>
            <div className="detail-item">
              <label>ACCOUNT CREATED</label>
              <span className="detail-value">
                {profile?.created_at ? new Date(profile.created_at).toLocaleDateString() : '-'}
              </span>
            </div>
            <div className="detail-item">
              <label>LAST LOGIN</label>
              <span className="detail-value">
                {profile?.last_login_at ? new Date(profile.last_login_at).toLocaleString() : '-'}
              </span>
            </div>
          </div>
        </div>
      </div>

      <style>{`
        .profile-page {
          padding: 0;
          width: 100%;
          min-height: 100vh;
          background: #f3f4f6;
        }

        .profile-header {
          background: var(--primary-gradient, linear-gradient(to right, #022a5e, #053e88, #022a5e));
          padding: 1.5rem 2rem;
          display: flex;
          align-items: center;
          gap: 1.5rem;
          border-radius: 12px;
          margin: 1.5rem;
          margin-bottom: 0;
        }

        /* White Theme Header (TFI/Throwball) */
        .profile-header.white-theme {
          background: #ffffff !important;
          border: 1px solid #fecaca !important;
          box-shadow: 0 2px 8px rgba(0, 0, 0, 0.08) !important;
          padding-left: 30px;
        }

        .profile-header.white-theme .btn-back {
          border: none;
          background: #c00f15;
          color: #ffffff;
        }

        .profile-header.white-theme .btn-back:hover {
          background: #a00d12;
        }

        .profile-header.white-theme .profile-avatar {
          background: #fef2f2;
          border: 1px solid #fecaca;
        }

        .profile-header.white-theme .profile-avatar i {
          color: #c00f15;
        }

        .profile-header.white-theme .profile-header-info h1 {
          color: #c00f15;
        }

        .profile-header.white-theme .profile-header-info p {
          color: #6b7280;
        }

        .profile-header.white-theme .btn-edit {
          background: linear-gradient(135deg, #c00f15 0%, #a00d12 100%);
          color: white;
        }

        .profile-header.white-theme .btn-edit:hover {
          background: linear-gradient(135deg, #a00d12 0%, #8b0a0f 100%);
          box-shadow: 0 4px 12px rgba(192, 15, 21, 0.3);
        }

        .btn-back {
          height: 40px;
          padding: 0 18px;
          border-radius: 8px;
          border: none;
          background: #c00f15;
          cursor: pointer;
          display: flex;
          align-items: center;
          gap: 8px;
          justify-content: center;
          color: white;
          font-weight: 600;
          font-size: 0.9rem;
          white-space: nowrap;
          transition: all 0.2s;
        }

        .btn-back:hover {
          background: rgba(255,255,255,0.2);
          border-color: rgba(255,255,255,0.5);
        }

        .profile-avatar {
          width: 60px;
          height: 60px;
          background: rgba(255,255,255,0.15);
          border-radius: 12px;
          display: flex;
          align-items: center;
          justify-content: center;
        }

        .profile-avatar i {
          font-size: 1.75rem;
          color: white;
        }

        .profile-header-info {
          flex: 1;
        }

        .profile-header-info h1 {
          font-size: 1.5rem;
          font-weight: 600;
          color: white;
          margin: 0 0 0.25rem 0;
        }

        .profile-header-info p {
          color: rgba(255,255,255,0.7);
          margin: 0;
          font-size: 0.875rem;
        }

        .btn-edit {
          padding: 0.75rem 1.5rem;
          background: white;
          color: var(--primary-color, #022a5e);
          border: none;
          border-radius: 8px;
          cursor: pointer;
          font-weight: 500;
          display: flex;
          align-items: center;
          gap: 0.5rem;
          transition: all 0.2s;
        }

        .btn-edit:hover {
          background: #f3f4f6;
        }

        .quick-info-cards {
          display: grid;
          grid-template-columns: repeat(3, 1fr);
          gap: 1rem;
          padding: 1.5rem;
          padding-top: 1rem;
        }

        .info-card {
          background: white;
          border-radius: 12px;
          padding: 1.25rem;
          display: flex;
          align-items: center;
          gap: 1rem;
          box-shadow: 0 1px 3px rgba(0,0,0,0.1);
          border: 1px solid #e5e7eb;
        }

        .info-card-icon {
          width: 45px;
          height: 45px;
          background: var(--primary-rgba, #f3f4f6);
          border-radius: 10px;
          display: flex;
          align-items: center;
          justify-content: center;
          color: var(--primary-color, #022a5e);
          font-size: 1.25rem;
        }

        .info-card-icon.status-icon {
          background: #dcfce7;
          color: #16a34a;
        }

        .info-card-content h3 {
          font-size: 1rem;
          font-weight: 600;
          color: #111827;
          margin: 0 0 0.25rem 0;
          white-space: nowrap;
          overflow: hidden;
          text-overflow: ellipsis;
        }

        .info-card-content p {
          font-size: 0.8rem;
          color: #6b7280;
          margin: 0;
        }

        .status-badge {
          display: inline-block;
          padding: 0.25rem 0.75rem;
          border-radius: 20px;
          font-size: 0.8rem;
          font-weight: 500;
        }

        .status-badge.active {
          background: #dcfce7;
          color: #16a34a;
        }

        .status-badge.pending {
          background: #fef3c7;
          color: #d97706;
        }

        .status-badge.suspended {
          background: #fee2e2;
          color: #dc2626;
        }

        .profile-content {
          padding: 0 1.5rem 1.5rem;
        }

        .detail-section {
          background: white;
          border-radius: 12px;
          padding: 1.5rem;
          margin-bottom: 1rem;
          box-shadow: 0 1px 3px rgba(0,0,0,0.1);
        }

        .detail-section h3 {
          font-size: 1rem;
          font-weight: 600;
          color: var(--primary-color, #022a5e);
          margin: 0 0 1.25rem 0;
          display: flex;
          align-items: center;
          gap: 0.5rem;
          padding-bottom: 0.75rem;
          border-bottom: 1px solid #e5e7eb;
        }

        .detail-section h3 i {
          font-size: 0.9rem;
        }

        .detail-grid {
          display: grid;
          grid-template-columns: repeat(2, 1fr);
          gap: 1.5rem;
        }

        .detail-item {
          display: flex;
          flex-direction: column;
          gap: 0.5rem;
        }

        .detail-item.full-width {
          grid-column: span 2;
        }

        .detail-item label {
          font-size: 0.75rem;
          font-weight: 600;
          color: #6b7280;
          text-transform: uppercase;
          letter-spacing: 0.5px;
        }

        .detail-value {
          font-size: 1rem;
          color: #111827;
          display: flex;
          align-items: center;
          gap: 0.5rem;
        }

        .icon-sm {
          font-size: 0.875rem;
          color: #9ca3af;
        }

        .state-badge {
          display: inline-block;
          background: #eff6ff;
          color: var(--primary-color, #022a5e);
          padding: 0.25rem 0.75rem;
          border-radius: 6px;
          font-weight: 500;
        }

        .username-badge {
          background: var(--primary-rgba, rgba(2, 42, 94, 0.05));
          padding: 0.5rem 0.75rem;
          border-radius: 6px;
          font-family: monospace;
          font-size: 0.95rem;
        }

        .password-field {
          display: flex;
          align-items: center;
          gap: 0.75rem;
        }

        .password-value {
          background: var(--primary-rgba, rgba(2, 42, 94, 0.05));
          padding: 0.5rem 0.75rem;
          border-radius: 6px;
          font-family: monospace;
          font-size: 0.95rem;
        }

        .toggle-password-btn {
          width: 36px;
          height: 36px;
          border-radius: 8px;
          border: 1px solid #e5e7eb;
          background: white;
          color: #6b7280;
          cursor: pointer;
          display: flex;
          align-items: center;
          justify-content: center;
          transition: all 0.2s;
        }

        .toggle-password-btn:hover {
          background: var(--primary-color, #022a5e);
          color: white;
          border-color: var(--primary-color, #022a5e);
        }

        .role-badge {
          display: inline-block;
          background: #f0fdf4;
          color: #16a34a;
          padding: 0.25rem 0.75rem;
          border-radius: 6px;
          font-weight: 500;
        }

        @media (max-width: 768px) {
          .profile-header {
            flex-wrap: wrap;
            margin: 1rem;
          }

          .quick-info-cards {
            grid-template-columns: 1fr;
            padding: 1rem;
          }

          .detail-grid {
            grid-template-columns: 1fr;
          }

          .detail-item.full-width {
            grid-column: span 1;
          }

          .profile-content {
            padding: 0 1rem 1rem;
          }

          .btn-edit {
            width: 100%;
            justify-content: center;
          }
        }
      `}</style>
    </div>
  )
}

export default MyProfile
